/**
 * Typed WebSocket event system for the AI assistant.
 * Replaces raw window.addEventListener('escalationNew', ...) with type-safe helpers.
 */

// ─── Event payload types ─────────────────────────────────────────────

export interface EscalationNewPayload {
  id: string;
  status: string;
  route: string;
  visitor_name: string;
  visitor_email?: string;
  message: string;
  created_at: string;
}

export interface EscalationClaimedPayload {
  id: string;
  claimed_by: number;
}

export interface EscalationReroutedPayload {
  id: string;
  rerouted_to: string;
  rerouted_at: string;
}

export interface EscalationResolvedPayload {
  id: string;
  resolved_by: number;
}

export interface EscalationTransferredPayload {
  id: string;
  to_agent?: number;
  department?: string;
  status?: string;
}

export interface ChangeNotificationPayload {
  id: string;
  entity_type?: string;
  change_type?: string;
  aggregation_id?: string;
  aggregation?: { title: string; description?: string };
  is_read?: boolean;
}

// ─── Event names ─────────────────────────────────────────────────────

type EscalationEventMap = {
  escalationNew: EscalationNewPayload;
  escalationClaimed: EscalationClaimedPayload;
  escalationRerouted: EscalationReroutedPayload;
  escalationResolved: EscalationResolvedPayload;
  escalationTransferred: EscalationTransferredPayload;
};

type AIEventMap = EscalationEventMap & {
  changeNotificationReceived: ChangeNotificationPayload;
  systemUpdatesDismissed: undefined;
};

// ─── Typed listeners ─────────────────────────────────────────────────

/**
 * Subscribe to a typed AI event. Returns a cleanup function.
 *
 * Usage:
 *   const unsub = onAIEvent('escalationNew', (payload) => { ... });
 *   return unsub; // in useEffect cleanup
 */
export function onAIEvent<K extends keyof AIEventMap>(
  event: K,
  callback: (payload: AIEventMap[K]) => void
): () => void {
  const handler = (e: Event) => {
    callback((e as CustomEvent).detail as AIEventMap[K]);
  };
  window.addEventListener(event, handler);
  return () => window.removeEventListener(event, handler);
}

/**
 * Dispatch a typed AI event.
 */
export function dispatchAIEvent<K extends keyof AIEventMap>(
  event: K,
  ...args: AIEventMap[K] extends undefined ? [] : [AIEventMap[K]]
): void {
  window.dispatchEvent(new CustomEvent(event, { detail: args[0] }));
}
